// Reusable JobProgressViewer: collapses consecutive same-stage events and shows latest percent.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { JobEvent } from '../api/jobsApi';
import { fmtBytes as formatBytes } from '@/utils/format';

export interface JobProgressViewerProps {
  events: JobEvent[];
  latest: JobEvent | null;
  loading: boolean;
  error: any;
  percent: number | null;
  className?: string;
  compact?: boolean;
  onRetry?: () => void; // optional manual retry trigger (unused in current UI)
}

export const JobProgressViewer: React.FC<JobProgressViewerProps> = ({ events, latest, loading, error, percent, className, compact, onRetry }) => {
  // Determine current stage (latest with stage) and compute stage progress and overall progress.
  const current = latest;
  const currentStageRaw = (current?.stage || (current as any)?.status || 'pending');
  // Friendly stage label: replace underscores/hyphens with spaces
  const currentStage = String(currentStageRaw).replace(/[-_]+/g, ' ');
  const stageLower = String(currentStageRaw || currentStage).toLowerCase();
  const errLower = String((current as any)?.error || '').toLowerCase();
  const msgLower = String((current as any)?.message || '').toLowerCase();
  const isRateLimited = /rate[ _-]?limited/.test(stageLower) || /rate[ _-]?limited/.test(errLower) || /rate[ _-]?limited/.test(msgLower);
  const isStackFinal = !!(current as any)?.stack_final;
  // Treat generic failed/error as done EXCEPT when it's a rate-limited transient
  const isTerminalStage = /^(done|completed|success|job_completed)$/i.test(stageLower);
  const isFailedOrError = /^(error|failed)$/i.test(stageLower);
  const isDone = isStackFinal || isTerminalStage || (isFailedOrError && !isRateLimited);

  // Smoothly animate elapsed time based on latest.time_lapsed
  const baseStartRef = useRef<number | null>(null);
  const [elapsedTick, setElapsedTick] = useState<number>(0);
  useEffect(() => {
    const t = (current as any)?.time_lapsed;
    if (t != null && !isDone) {
      baseStartRef.current = Date.now() - Number(t) * 1000;
      setElapsedTick(0);
      const iv = setInterval(() => setElapsedTick(v => v + 1), 1000);
      return () => clearInterval(iv);
    } else {
      baseStartRef.current = null;
      setElapsedTick(0);
    }
  }, [current, isDone]);
  const animatedElapsed = useMemo(() => {
    if (baseStartRef.current == null) return (current as any)?.time_lapsed;
    return Math.round((Date.now() - baseStartRef.current) / 1000);
  }, [elapsedTick, current]);

  const stagePct = (() => {
    if (!current) return null;
    if (typeof (current as any).stage_pct === 'number') return (current as any).stage_pct;
    if (typeof (current as any).stage_progress === 'number') return Math.round((current as any).stage_progress * 100);
    return null;
  })();

  const overall = (() => {
    if (current && typeof (current as any).overall_pct === 'number') return (current as any).overall_pct;
    if (current && typeof (current as any).overall_progress === 'number') return Math.round((current as any).overall_progress * 100);
    if (percent != null) return percent;
    if (current && typeof (current as any).progress === 'number') return Math.round((current as any).progress * 100);
    return null;
  })();

  // Special handling for rate-limited states with countdown
  const baseWaitSeconds = (() => {
    if (!current) return null;
    const c: any = current;
    // Prefer explicit wait_seconds; accept alternate names
    const v = c.wait_seconds ?? c.retry_in_seconds ?? c.backoff_seconds ?? c.retry_after ?? c.wait_remain_sec ?? null;
    return (typeof v === 'number' ? v : (typeof v === 'string' ? Number(v) : null));
  })();
  const waitTargetRef = useRef<number | null>(null);
  const [waitTick, setWaitTick] = useState(0);
  useEffect(() => {
    if (isRateLimited && baseWaitSeconds != null && isFinite(baseWaitSeconds) && baseWaitSeconds > 0) {
      waitTargetRef.current = Date.now() + Number(baseWaitSeconds) * 1000;
      setWaitTick(0);
      const iv = setInterval(() => setWaitTick(v => v + 1), 1000);
      return () => clearInterval(iv);
    } else {
      waitTargetRef.current = null;
      setWaitTick(0);
    }
  }, [isRateLimited, baseWaitSeconds, current?.seq]);
  const waitRemainSeconds = useMemo(() => {
    if (!isRateLimited) return null;
    if (waitTargetRef.current == null) return baseWaitSeconds ?? null;
    const remain = Math.ceil((waitTargetRef.current - Date.now()) / 1000);
    return Math.max(0, remain);
  }, [waitTick, isRateLimited, baseWaitSeconds]);

  function formatWait(sec: number | null): string | null {
    if (sec == null || !isFinite(sec)) return null;
    const s = Math.max(0, Math.floor(sec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    if (h > 0) return `${h}h ${m}m ${r}s`;
    if (m > 0) return `${m}m ${r}s`;
    return `${r}s`;
  }

  return (
    <div className={className ?? ''}>
      <div className={`${compact ? 'text-[10px]' : 'text-[11px]'}`}>
        {events.length === 0 && (
          <div className="py-2 px-3 text-neutral-600">(Starting processing — setting things up...)</div>
        )}

        {events.length > 0 && isRateLimited && (
          <div className="py-2 px-3 rounded-md pac-glass/60">
            <div className="text-amber-400 leading-snug">
              {waitRemainSeconds != null
                ? `Limit reached - Retry in ${formatWait(waitRemainSeconds)}`
                : 'Limit reached - Retrying...'}
            </div>
          </div>
        )}

        {events.length > 0 && !isRateLimited && (
          <div className="py-2 px-3 rounded-md pac-glass/60">
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-4 min-w-0">
                <div className="text-neutral-300 font-medium tracking-wide capitalize truncate flex items-center gap-2">
                  <span>{currentStage}</span>
                  {loading && <span className="text-neutral-500 animate-pulse">live</span>}
                  {overall != null && <span className="text-neutral-400 tabular-nums">{overall}%</span>}
                </div>
                {/* When job is just starting, show a friendly status line */}
                {current && stageLower === 'job_started' && (
                  <div className="text-neutral-500 truncate leading-snug mt-0.5">Preparing your environment...</div>
                )}
                {/* Step row first (above phase), per requested order */}
                {current && (current as any).stack_steps != null && (
                  <div className="text-neutral-500 truncate leading-snug mt-0.5">
                      Step {((current as any).stack_current ?? ((current as any).stack_step_index ?? 0) + 1)} / {(current as any).stack_steps}
                    { (current as any).stack_completed_steps != null && <span> ({(current as any).stack_completed_steps} done)</span> }
                    { (animatedElapsed != null) && <span className="ml-2 text-neutral-600">⏱ {animatedElapsed}s</span> }
                  </div>
                )}
                {/* Phase row: "phase X/Y" (no phase name here) */}
                {current && ((current as any).phase_current != null && (current as any).phase_total != null) && (
                  <div className="text-neutral-500 truncate leading-snug mt-0.5">
                    <span className="lowercase">Phase {(current as any).phase_current}/{(current as any).phase_total}</span>
                    {(current as any).phase_completed === true && <span className="ml-2 text-emerald-500">done</span>}
                  </div>
                )}
                {current && (current as any).stack_steps == null && (animatedElapsed != null) && (
                  <div className="text-neutral-600 truncate leading-snug mt-0.5">⏱ {animatedElapsed}s</div>
                )}
                {current?.message && (
                  <div className="text-neutral-500 truncate leading-snug mt-0.5">{current.message}</div>
                )}
                {current && (current as any).error && (
                  <div className="text-rose-400 truncate leading-snug mt-0.5">{(current as any).error}</div>
                )}
              </div>
              {stagePct != null && (
                <div className="w-20 text-right">
                  <span className="text-neutral-400 tabular-nums text-[10px]">{stagePct}%</span>
                </div>
              )}
            </div>
            {stagePct != null && !/^(done|completed|success)$/i.test(currentStage) && !/^(error|failed)$/i.test(currentStage) && (
              <div className="mt-2 h-1 w-full rounded bg-neutral-800/70 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-neutral-600 to-neutral-400 transition-all duration-300" style={{ width: `${stagePct}%` }} />
              </div>
            )}
            {/* Upload stats (bytes) when present */}
            {current && ((current as any).uploaded != null && (current as any).total != null) && (
              <div className="mt-2 text-[10px] text-neutral-500 flex items-center gap-2">
                <span>{formatBytes((current as any).uploaded)} / {formatBytes((current as any).total)}</span>
                {(current as any).stem && <span className="truncate">• {(current as any).stem}</span>}
              </div>
            )}
            {error && (<div className="mt-2 text-rose-400 text-[11px]" role="alert">{String(error)}</div>)}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobProgressViewer;
